import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  IconButton,
  Divider,
  CircularProgress,
} from '@mui/material';
import { Close, Visibility } from '@mui/icons-material';

import { formatOfficialDateBR, todayOfficialDateKey } from '../utils/officialDate';
import { KEYWORD_SEARCH_SCOPE_INFO } from '../utils/keywordSearch';

export interface KeywordMonitoringConfirmDialogProps {
  open: boolean;
  keywordLabel: string | null;
  userName?: string | null;
  onClose: () => void;
  onConfirm: () => void;
  loading?: boolean;
}

export function KeywordMonitoringConfirmDialog({
  open,
  keywordLabel,
  userName,
  onClose,
  onConfirm,
  loading = false,
}: KeywordMonitoringConfirmDialogProps) {
  const todayLabel = formatOfficialDateBR(todayOfficialDateKey());

  return (
    <Dialog open={open} onClose={loading ? undefined : onClose} maxWidth="xs" fullWidth>
      <DialogTitle sx={{ pb: 1 }}>
        <Box display="flex" alignItems="flex-start" justifyContent="space-between" gap={1}>
          <Box display="flex" alignItems="center" gap={1}>
            <Visibility color="success" />
            <Typography variant="h6" fontWeight={600}>
              Confirmar monitoramento
            </Typography>
          </Box>
          <IconButton onClick={onClose} size="small" disabled={loading} aria-label="Fechar">
            <Close />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent dividers>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Revise a palavra-chave que será monitorada antes de salvar.
        </Typography>

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
          <Box>
            <Typography variant="caption" color="text.secondary">
              Palavra-chave
            </Typography>
            <Typography variant="body1" fontWeight={700}>
              {keywordLabel ?? '—'}
            </Typography>
          </Box>

          <Divider />

          <Typography variant="body2">
            Serão acompanhadas as <strong>mudanças legislativas</strong> cujo <strong>título</strong> ou{' '}
            <strong>ementa</strong> contenham a palavra-chave <strong>{keywordLabel}</strong>, a partir de agora.
          </Typography>

          <Box>
            <Typography variant="caption" color="text.secondary">
              Solicitado por
            </Typography>
            <Typography variant="body2" fontWeight={600}>
              {userName?.trim() || 'Usuário atual'}
            </Typography>
          </Box>

          <Box>
            <Typography variant="caption" color="text.secondary">
              Data
            </Typography>
            <Typography variant="body2">{todayLabel}</Typography>
          </Box>

          <Typography variant="caption" color="text.secondary">
            {KEYWORD_SEARCH_SCOPE_INFO}
          </Typography>
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2, gap: 1 }}>
        <Button onClick={onClose} disabled={loading} sx={{ textTransform: 'none' }}>
          Cancelar
        </Button>
        <Button
          variant="contained"
          color="success"
          onClick={onConfirm}
          disabled={loading || !keywordLabel}
          startIcon={loading ? <CircularProgress size={16} color="inherit" /> : <Visibility />}
          sx={{ textTransform: 'none', fontWeight: 600 }}
        >
          {loading ? 'Salvando…' : 'Confirmar e salvar'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
